/**
 * User list page — FE-03.
 * Search, filter by status/plan, sortable paginated table, click to detail.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  type AdminUserSummary,
  type UserListStats,
  type UserSortDirection,
  type UserSortField,
  type UserStatus,
  fetchUsers,
  normalizeUserListStats,
} from "../../api/admin-users";
import { PageHeader } from "../../components/PageHeader";
import { StatsBar } from "../../components/StatsBar";
import { t } from "../../i18n";

const PAGE_SIZE = 20;
const STATUS_OPTIONS = ["all", "active", "frozen", "deactivated"] as const;
const PLAN_OPTIONS = ["all", "free", "pro", "premium", "max"] as const;

type StatusFilter = (typeof STATUS_OPTIONS)[number];
type PlanFilter = (typeof PLAN_OPTIONS)[number];

export function UserListPage(): React.JSX.Element {
  const navigate = useNavigate();
  // queryInput is the live text box; queryFilter is the applied search term.
  // Separating them stops a refetch firing on every keystroke (the input no
  // longer participates in loadUsers' dependency list).
  const [queryInput, setQueryInput] = useState("");
  const [queryFilter, setQueryFilter] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [plan, setPlan] = useState<PlanFilter>("all");
  const [sortBy, setSortBy] = useState<UserSortField>("created_at");
  const [sortDir, setSortDir] = useState<UserSortDirection>("desc");
  const [offset, setOffset] = useState(0);
  const [items, setItems] = useState<AdminUserSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<UserListStats>(() =>
    normalizeUserListStats(null, 0),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const loadUsers = useCallback(async (): Promise<void> => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchUsers({
        q: queryFilter || undefined,
        status: status === "all" ? undefined : (status as UserStatus),
        plan: plan === "all" ? undefined : plan,
        sort_by: sortBy,
        sort_dir: sortDir,
        limit: PAGE_SIZE,
        offset,
      });
      // Drop stale responses: a newer request superseded this one.
      if (requestId !== requestIdRef.current) return;
      setItems(res.items);
      setTotal(res.total);
      setStats(res.stats);
    } catch (e) {
      if (requestId !== requestIdRef.current) return;
      setError(
        e instanceof Error ? e.message : t("user_detail.failed_to_load"),
      );
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [queryFilter, status, plan, sortBy, sortDir, offset]);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  const handleSearch = useCallback((): void => {
    const next = queryInput.trim();
    if (next === queryFilter && offset === 0) {
      // Same query + first page: deps won't change, so refetch explicitly.
      void loadUsers();
    } else {
      setQueryFilter(next);
      setOffset(0);
    }
  }, [queryInput, queryFilter, offset, loadUsers]);

  const handleStatusChange = useCallback((v: StatusFilter): void => {
    setStatus(v);
    setOffset(0);
  }, []);

  const handlePlanChange = useCallback((v: PlanFilter): void => {
    setPlan(v);
    setOffset(0);
  }, []);

  const handleSortChange = useCallback(
    (field: UserSortField): void => {
      if (field === sortBy) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortBy(field);
        setSortDir(defaultSortDirection(field));
      }
      setOffset(0);
    },
    [sortBy],
  );

  return (
    <div>
      <PageHeader
        title={t("users.title")}
        description={t("users.description")}
      />
      <StatsBar
        items={userListStatItems(stats).map((item) => ({
          label: t(item.labelKey),
          value: item.value,
        }))}
      />
      <FilterBar
        query={queryInput}
        status={status}
        plan={plan}
        onQueryChange={setQueryInput}
        onStatusChange={handleStatusChange}
        onPlanChange={handlePlanChange}
        onSearch={handleSearch}
      />
      {error && (
        <p role="alert" style={s.error}>
          {error}
        </p>
      )}
      {loading ? (
        <output aria-live="polite" style={s.loading}>
          {t("users.loading")}
        </output>
      ) : (
        <>
          <UserTable
            items={items}
            sortBy={sortBy}
            sortDir={sortDir}
            onSortChange={handleSortChange}
            onRowClick={(id) => navigate(`/users/${id}`)}
          />
          <Pagination offset={offset} total={total} onChange={setOffset} />
        </>
      )}
    </div>
  );
}

// ─── Stats + sort helpers ───

function userListStatItems(
  stats: UserListStats,
): ReadonlyArray<{ labelKey: string; value: number }> {
  return [
    { labelKey: "stats.total", value: stats.total },
    { labelKey: "users.status_active", value: stats.by_status.active },
    { labelKey: "users.status_frozen", value: stats.by_status.frozen },
    {
      labelKey: "users.status_deactivated",
      value: stats.by_status.deactivated,
    },
    { labelKey: "users.plan_free", value: stats.by_plan.free },
    { labelKey: "users.plan_pro", value: stats.by_plan.pro },
    { labelKey: "users.plan_premium", value: stats.by_plan.premium },
    { labelKey: "users.plan_max", value: stats.by_plan.max },
  ];
}

function defaultSortDirection(field: UserSortField): UserSortDirection {
  return field === "created_at" || field === "token_balance" ? "desc" : "asc";
}

// ─── FilterBar ───

type FilterBarProps = {
  query: string;
  status: StatusFilter;
  plan: PlanFilter;
  onQueryChange: (v: string) => void;
  onStatusChange: (v: StatusFilter) => void;
  onPlanChange: (v: PlanFilter) => void;
  onSearch: () => void;
};

function FilterBar(p: FilterBarProps): React.JSX.Element {
  return (
    <form
      style={s.filterBar}
      onSubmit={(e) => {
        e.preventDefault();
        p.onSearch();
      }}
    >
      <input
        type="text"
        aria-label={t("users.search_placeholder")}
        placeholder={t("users.search_placeholder")}
        value={p.query}
        onChange={(e) => p.onQueryChange(e.target.value)}
        style={s.searchInput}
      />
      <select
        value={p.status}
        onChange={(e) => p.onStatusChange(coerceStatus(e.target.value))}
        style={s.select}
        aria-label="Filter by status"
      >
        {STATUS_OPTIONS.map((v) => (
          <option key={v} value={v}>
            {v === "all" ? t("users.all_status") : capitalize(v)}
          </option>
        ))}
      </select>
      <select
        value={p.plan}
        onChange={(e) => p.onPlanChange(coercePlan(e.target.value))}
        style={s.select}
        aria-label="Filter by plan"
      >
        {PLAN_OPTIONS.map((v) => (
          <option key={v} value={v}>
            {v === "all" ? t("users.all_plans") : capitalize(v)}
          </option>
        ))}
      </select>
      <button type="submit" style={s.searchBtn}>
        {t("common.search")}
      </button>
    </form>
  );
}

function coerceStatus(value: string): StatusFilter {
  return (STATUS_OPTIONS as ReadonlyArray<string>).includes(value)
    ? (value as StatusFilter)
    : "all";
}

function coercePlan(value: string): PlanFilter {
  return (PLAN_OPTIONS as ReadonlyArray<string>).includes(value)
    ? (value as PlanFilter)
    : "all";
}

// ─── Table ───

const TABLE_COLUMNS: ReadonlyArray<{
  readonly labelKey: string;
  readonly sort: UserSortField | null;
}> = [
  { labelKey: "users.col_name", sort: "display_name" },
  { labelKey: "users.col_email", sort: "email" },
  { labelKey: "users.col_custom_id", sort: null },
  { labelKey: "users.col_plan", sort: "plan_type" },
  { labelKey: "users.col_tokens", sort: "token_balance" },
  { labelKey: "users.col_storage", sort: null },
  { labelKey: "users.col_status", sort: null },
  { labelKey: "users.col_created", sort: "created_at" },
];

function UserTable(props: {
  items: AdminUserSummary[];
  sortBy: UserSortField;
  sortDir: UserSortDirection;
  onSortChange: (field: UserSortField) => void;
  onRowClick: (id: string) => void;
}): React.JSX.Element {
  if (props.items.length === 0)
    return <p style={s.empty}>{t("users.no_users_found")}</p>;
  return (
    <div style={s.tableWrap}>
      <table style={s.table} aria-label={t("users.title")}>
        <thead>
          <tr>
            {TABLE_COLUMNS.map((column) => (
              <UserHeaderCell
                key={column.labelKey}
                column={column}
                sortBy={props.sortBy}
                sortDir={props.sortDir}
                onSortChange={props.onSortChange}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {props.items.map((u) => (
            <UserRow key={u.id} user={u} onClick={props.onRowClick} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UserHeaderCell(props: {
  column: { readonly labelKey: string; readonly sort: UserSortField | null };
  sortBy: UserSortField;
  sortDir: UserSortDirection;
  onSortChange: (field: UserSortField) => void;
}): React.JSX.Element {
  const { column } = props;
  const label = t(column.labelKey);
  if (!column.sort) {
    return (
      <th scope="col" style={s.th}>
        {label}
      </th>
    );
  }
  const sortField = column.sort;
  const active = props.sortBy === sortField;
  return (
    <th
      scope="col"
      aria-sort={active ? sortAriaValue(props.sortDir) : "none"}
      style={s.th}
    >
      <button
        type="button"
        style={s.sortHeaderButton}
        onClick={() => props.onSortChange(sortField)}
        aria-label={t("users.sort_by", { label })}
        title={t("users.sort_by", { label })}
      >
        <span>{label}</span>
        <span aria-hidden="true" style={s.sortIndicator}>
          {active ? sortIndicator(props.sortDir) : ""}
        </span>
      </button>
    </th>
  );
}

function sortAriaValue(
  direction: UserSortDirection,
): "ascending" | "descending" {
  return direction === "asc" ? "ascending" : "descending";
}

function sortIndicator(direction: UserSortDirection): string {
  return direction === "asc" ? "▲" : "▼";
}

function UserRow(props: {
  user: AdminUserSummary;
  onClick: (id: string) => void;
}): React.JSX.Element {
  const { user: u } = props;
  return (
    <tr
      style={{ ...s.tr, cursor: "pointer" }}
      onClick={() => props.onClick(u.id)}
      tabIndex={0}
      aria-label={u.display_name}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          props.onClick(u.id);
        }
      }}
    >
      <td style={s.td}>{u.display_name}</td>
      <td style={s.td}>{u.email}</td>
      <td style={{ ...s.td, fontFamily: "var(--font-mono)", fontSize: 12 }}>
        {u.custom_id ?? "-"}
      </td>
      <td style={s.td}>{capitalize(u.plan_type)}</td>
      <td style={s.tdRight}>{u.token_balance.toLocaleString()}</td>
      <td style={s.tdRight}>
        {formatStorage(u.storage_used_bytes, u.storage_limit_bytes)}
      </td>
      <td style={s.td}>
        <StatusBadge status={u.status} />
      </td>
      <td style={s.td}>{formatDate(u.created_at)}</td>
    </tr>
  );
}

// ─── Pagination ───

function Pagination(props: {
  offset: number;
  total: number;
  onChange: (v: number) => void;
}): React.JSX.Element {
  const page = Math.floor(props.offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(props.total / PAGE_SIZE));
  return (
    <div style={s.pagination}>
      <span style={s.pageInfo}>
        {t("users.page_info", { total: props.total, page, totalPages })}
      </span>
      <div style={s.pageButtons}>
        <button
          type="button"
          disabled={props.offset === 0}
          onClick={() => props.onChange(Math.max(0, props.offset - PAGE_SIZE))}
          style={s.pageBtn}
        >
          {t("common.previous")}
        </button>
        <button
          type="button"
          disabled={props.offset + PAGE_SIZE >= props.total}
          onClick={() => props.onChange(props.offset + PAGE_SIZE)}
          style={s.pageBtn}
        >
          {t("common.next")}
        </button>
      </div>
    </div>
  );
}

// ─── StatusBadge ───

function StatusBadge({ status }: { status: UserStatus }): React.JSX.Element {
  const color = STATUS_COLOR[status];
  return (
    <span style={{ ...s.badge, background: color.bg, color: color.fg }}>
      {capitalize(status)}
    </span>
  );
}

const STATUS_COLOR: Record<UserStatus, { bg: string; fg: string }> = {
  active: { bg: "#dcfce7", fg: "#166534" },
  frozen: { bg: "#fef2f2", fg: "#991b1b" },
  deactivated: { bg: "#f3f4f6", fg: "#6b7280" },
};

// ─── Helpers ───

function capitalize(v: string): string {
  return v.charAt(0).toUpperCase() + v.slice(1);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatStorage(used: number, limit: number): string {
  const fmt = (b: number): string => {
    if (b >= 1_073_741_824) return `${(b / 1_073_741_824).toFixed(1)} GB`;
    if (b >= 1_048_576) return `${(b / 1_048_576).toFixed(1)} MB`;
    return `${(b / 1_024).toFixed(0)} KB`;
  };
  return limit > 0 ? `${fmt(used)} / ${fmt(limit)}` : fmt(used);
}

// ─── Styles ───

const s: Record<string, React.CSSProperties> = {
  filterBar: { display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" },
  searchInput: {
    flex: 1,
    minWidth: 200,
    padding: "8px 12px",
    fontSize: 13,
    border: "1px solid var(--color-border)",
    borderRadius: 6,
  },
  select: {
    padding: "8px 12px",
    fontSize: 13,
    borderRadius: 6,
    border: "1px solid var(--color-border)",
    background: "var(--color-bg)",
  },
  searchBtn: {
    padding: "8px 16px",
    fontSize: 13,
    fontWeight: 500,
    borderRadius: 6,
    background: "var(--color-primary)",
    color: "#fff",
  },
  loading: { fontSize: 14, color: "var(--color-text-secondary)", padding: 24 },
  error: { fontSize: 14, color: "var(--color-danger)", padding: "8px 0" },
  empty: {
    fontSize: 14,
    color: "var(--color-text-secondary)",
    padding: 24,
    textAlign: "center",
  },
  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    textAlign: "left",
    padding: "10px 12px",
    fontSize: 12,
    fontWeight: 600,
    color: "var(--color-text-secondary)",
    borderBottom: "1px solid var(--color-border)",
    whiteSpace: "nowrap",
  },
  sortHeaderButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    background: "none",
    border: "none",
    padding: 0,
    margin: 0,
    font: "inherit",
    color: "inherit",
    cursor: "pointer",
  },
  sortIndicator: { fontSize: 9 },
  td: {
    padding: "10px 12px",
    fontSize: 13,
    borderBottom: "1px solid var(--color-border)",
    whiteSpace: "nowrap",
  },
  tdRight: {
    padding: "10px 12px",
    fontSize: 13,
    borderBottom: "1px solid var(--color-border)",
    whiteSpace: "nowrap",
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
  },
  tr: { cursor: "pointer" },
  badge: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 600,
  },
  pagination: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 0",
  },
  pageInfo: { fontSize: 13, color: "var(--color-text-secondary)" },
  pageButtons: { display: "flex", gap: 8 },
  pageBtn: {
    padding: "6px 14px",
    fontSize: 13,
    borderRadius: 6,
    border: "1px solid var(--color-border)",
    background: "var(--color-bg)",
  },
};
